import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Paper, Container, Alert } from '@mui/material';
import canvaIntegrationService from '../services/canvaIntegrationService';

const CanvaCallback: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<boolean>(true);
  const [debugInfo, setDebugInfo] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      try {
         const searchParams = new URLSearchParams(location.search);
         const code = searchParams.get('code');
         const state = searchParams.get('state');
         const errorParam = searchParams.get('error');
         const errorDescription = searchParams.get('error_description');

         setDebugInfo(
            JSON.stringify({
               code: code?.substring(0, 5) + '...',
               state,
               error: errorParam,
               errorDescription,
               search: location.search,
            })
         );

         if (errorParam) {
            setError(
               `Authentication failed: ${errorParam}${errorDescription ? ` - ${errorDescription}` : ''}`
            );
            setProcessing(false);
            return;
         }

         if (!code) {
            setError('No authorization code received from Canva');
            setProcessing(false);
            return;
         }

         // Exchange code for token
         const token = await canvaIntegrationService.exchangeCodeForToken(code);

         // Store token in localStorage
         localStorage.setItem('canva_access_token', token);

         // Redirect back to the referring page or dashboard
         const redirectPath = localStorage.getItem('canva_redirect_path') || '/organizations';
         navigate(redirectPath);
      } catch (err: any) {
        console.error('Error processing Canva callback:', err);
        setError(`Failed to complete Canva authentication: ${err.message || 'Unknown error'}. Please try again.`);
        setProcessing(false);
      }
    };

    handleCallback();
  }, [location, navigate]);

  return (
    <Container maxWidth="sm">
      <Paper sx={{ p: 4, mt: 8, textAlign: 'center' }}>
        {processing ? (
          <>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              Completing Canva Authentication
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please wait while we complete the authentication process...
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h5" color="error" gutterBottom>
              Authentication Error
            </Typography>
            <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
              {error}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Debug information: {debugInfo}
            </Typography>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default CanvaCallback;
